import { GetProjectsFilter, ProjectSummary, ProjectType } from '../../../../common/models/project';
import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";

import { MatExpansionModule } from "@angular/material/expansion";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { CurrentUser } from "../../../../common/models/user";
import { ProjectService } from "../../../../common/services/project.service";
import { StorageService } from "../../../../common/services/storage.service";
import { SearchService } from "../../../../common/services/search.service";
import { CustomizerSettingsService } from "../../../../customizer-settings/customizer-settings.service";
import { ProjectsFilterBuilder } from '../../../../common/components/filter/builders/projects-filter-builder';
import { FilterService } from '../../../../common/components/filter/services/filter.service';
import { Subject, takeUntil } from 'rxjs';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ProjectStatus } from '../../../../common/enums/project-status';
import { MatDialog } from '@angular/material/dialog';
import { GenericDialogComponent } from '../../../../common/components/dialog/dialog';

@Component({
    selector: 'app-projects',
    templateUrl: './projects.component.html',
    styleUrls: ['./projects.component.scss'],
    imports: [MatCardModule,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        MatExpansionModule,
        MatCheckboxModule,
        ReactiveFormsModule,
        CommonModule]
})
export class ProjectsComponent implements OnInit {
    private destroy$ = new Subject<void>();

    public ProjectStatus = ProjectStatus;
    public ProjectType = ProjectType;
   
    projects: ProjectSummary[] = [];
    filteredProjects: ProjectSummary[] = [];
    user: CurrentUser | null = null;
  
    isToggled = false;
    
    showLifecycleCtrl = new FormControl(false, { nonNullable: true });

    private readonly snackbarConfig: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    };
  
    constructor(
      private router: Router,
      private filterBuilder: ProjectsFilterBuilder,
      private projectService: ProjectService,
      private storageService: StorageService,
      private searchService: SearchService,
      private filterService: FilterService,
      public themeService: CustomizerSettingsService,
      private snackBar: MatSnackBar,
      private dialog: MatDialog
    ) {}
  
    ngOnInit() {
      this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
        this.isToggled = isToggled;
      });
      this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
        this.applySearch(term);
      });
      this.filterService.filterChanges$.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
        this.filterService.setFiltersInScope(filters);
        this.fetchProjects();
      });
      this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
        this.user = user;
        this.initializeFilters()
      });
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

    initializeFilters(): void {
      const startYear = 2025;
      const currentYear = new Date().getFullYear();
    
      const config = this.filterBuilder
        .reset()
        .years(startYear)
        .type()
        .status()
        .initialBudgetInSync()
        .initialBudgetApproved()
        .hasReallocation()
        .reallocationBudgetsInSync()
        .reallocationBudgetsApproved()
        .hasContract()
        .reportsApproved()
        .hasRefundItems()
        .hasRefundDecision()
        .build();

        this.filterService.setFilterConfig(config);
        this.filterService.restoreFiltersInScope({ year: currentYear });
    }
  
    fetchProjects(): void {
      const savedFilterValues = this.filterService.getFiltersInScope();
      const municipalityId = this.user?.municipalityId ?? '';

      var filter = new GetProjectsFilter(
        municipalityId,
        undefined,
        savedFilterValues?.year, 
        undefined,
        savedFilterValues?.type,
        savedFilterValues?.status,
        savedFilterValues?.isInitialBudgetInSyncFilter,
        savedFilterValues?.isInitialBudgetApprovedFilter,
        savedFilterValues?.hasReallocationFilter,
        savedFilterValues?.areReallocationBudgetsInSyncFilter,
        savedFilterValues?.areReallocationBudgetsApprovedFilter,
        savedFilterValues?.contractCreatedFilter,
        savedFilterValues?.reportsApprovedFilter,
        savedFilterValues?.hasRefundItemsFilter,
        savedFilterValues?.refundCreatedFilter
     );

      this.projectService.getProjects(filter).pipe(takeUntil(this.destroy$)).subscribe({
        next: (data) => {
          this.projects = data.map(a => ProjectSummary.fromJson(a));
          this.filteredProjects = [...this.projects];
        },
        error: (error) => {
          console.error('Error fetching projects', error);
        }
      });
    }

    confirmStart(project: ProjectSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite puštanje u provedbu',
          message: `Jeste li sigurni da želite pustiti projekt "${project.name}" u provedbu?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.startProject(project);
        }
      });
    }

    private startProject(project: ProjectSummary): void {
          this.projectService.changeProjectStatus(project.id, { targetStatus: ProjectStatus.Started }).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
              this.snackBar.open('Projekt je pušten u provedbu', 'Zatvori', this.snackbarConfig);
              this.fetchProjects();
            },
            error: (error) => {
              console.error('Greška prilikom puštanja projekta u provedbu', error);
            }
          });
    }

   confirmComplete(project: ProjectSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite završetak projekta',
          message: `Jeste li sigurni da želite završiti projekt "${project.name}"?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.completeProject(project);
        }
      });
    }

   private completeProject(project: ProjectSummary): void {
      this.projectService.changeProjectStatus(project.id, { targetStatus: ProjectStatus.Completed }).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Projekt je završen', 'Zatvori', this.snackbarConfig);
          this.fetchProjects();
        },
        error: (error) => {
          console.error('Greška prilikom završavanja projekta', error);
        }
      });
   }

    
    applySearch(searchTerm: string): void {
      searchTerm = searchTerm.trim().toLowerCase();
      if (searchTerm) {
        this.filteredProjects = this.projects.filter((project: ProjectSummary) => {
          return (
            project.organizationName.toLowerCase().includes(searchTerm) ||
            project.name.toLowerCase().includes(searchTerm) ||
            project.year.toString().includes(searchTerm)
          );
        });
      } else {
        this.filteredProjects = [...this.projects];
      }
    }

    goToProjectSaldo(project: ProjectSummary): void {
       this.router.navigate(['municipality', 'projects', project.id.toString(), 'saldo']);
    }

    goToViewContracts(project: ProjectSummary): void {
      this.router.navigate(['municipality', 'projects', project.id.toString(), 'contracts'], {
        state: { projectType: project.type }
      });
    }

    goToViewRefunds(project: ProjectSummary): void {
      this.router.navigate(['municipality', 'projects', project.id.toString(), 'refunds']);
    }

    goToEditProject(project: ProjectSummary): void {
      this.router.navigate(['municipality', 'projects', project.id.toString(), 'edit']);
    }

    goToProjectReports(project: ProjectSummary): void {
      this.router.navigate(['municipality', 'projects', project.id.toString(), 'reports']);
    }
    
    goToProjectBudgets(project: ProjectSummary): void {
      this.router.navigate(['municipality', 'projects', project.id.toString(), 'budgets']);
    }

  }
